import {
  VOICE_LIVE_CALL_END,
  VOICE_LIVE_CALL_MARK_ANSWERED,
  VOICE_LIVE_CALL_PURGE,
  VOICE_LIVE_CALL_UPSERT,
  VOICE_LIVE_CALLS_CLEAR,
  VOICE_LIVE_CALLS_SET_SNAPSHOT
} from '../../actions/voice/voiceLiveCallsActions';

/** @type {{ calls: Array<object>, serverTime: string|null }} */
const initialState = {
  calls: [],
  serverTime: null
};

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function readString(value) {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

/**
 * Accepts camelCase (socket) and snake_case (snapshot API) fragments.
 *
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeVoiceLiveCallEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (raw);
  const callControlId = readString(record.callControlId ?? record.call_control_id);
  if (!callControlId) return null;

  const startedAt = readString(record.startedAt ?? record.started_at ?? record.timestamp);
  const answeredAt = readString(record.answeredAt ?? record.answered_at);
  const state = readString(record.state);

  return {
    callControlId,
    from: readString(record.from),
    to: readString(record.to),
    direction: readString(record.direction) || '',
    channel: readString(record.channel) || 'unknown',
    state: state === 'in_progress' || state === 'ended' ? state : 'ringing',
    claimedByUserId: record.claimedByUserId ?? record.claimed_by_user_id ?? null,
    answeredByUserId: record.answeredByUserId ?? record.answered_by_user_id ?? null,
    startedAt: startedAt || new Date().toISOString(),
    answeredAt,
    endedAt: readString(record.endedAt ?? record.ended_at)
  };
}

/**
 * @param {object|null} entry
 * @returns {number}
 */
function entryRecencyMs(entry) {
  const parsed = Date.parse(entry?.startedAt || entry?.answeredAt || '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

function sortByRecencyDesc(calls) {
  return [...calls].sort((a, b) => entryRecencyMs(b) - entryRecencyMs(a));
}

/**
 * @param {Array<object>} calls
 * @param {object} incoming
 * @returns {Array<object>}
 */
function upsertEntry(calls, incoming) {
  const index = calls.findIndex((call) => call.callControlId === incoming.callControlId);
  if (index === -1) {
    return sortByRecencyDesc([incoming, ...calls]);
  }
  const existing = calls[index];
  const isStaleRingAfterEnd = existing.state === 'ended' && incoming.state === 'ringing';
  const merged = isStaleRingAfterEnd
    ? {
      ...existing,
      ...incoming,
      state: existing.state,
      endedAt: existing.endedAt
    }
    : { ...existing, ...incoming };
  const next = [...calls];
  next[index] = merged;
  return next;
}

export default function voiceLiveCallsReducer(state = initialState, action) {
  switch (action.type) {
    case VOICE_LIVE_CALL_UPSERT: {
      const incoming = normalizeVoiceLiveCallEntry(action.payload);
      if (!incoming) return state;
      return { ...state, calls: upsertEntry(state.calls, incoming) };
    }
    case VOICE_LIVE_CALL_MARK_ANSWERED: {
      const callControlId = readString(action.payload?.callControlId);
      if (!callControlId) return state;
      const patch = normalizeVoiceLiveCallEntry({
        callControlId,
        from: action.payload?.from,
        to: action.payload?.to,
        direction: action.payload?.direction,
        channel: action.payload?.channel,
        state: 'in_progress',
        startedAt: action.payload?.startedAt,
        answeredAt: action.payload?.answeredAt || new Date().toISOString(),
        answeredByUserId: action.payload?.answeredByUserId ?? null
      });
      return { ...state, calls: upsertEntry(state.calls, patch) };
    }
    case VOICE_LIVE_CALL_END: {
      const callControlId = readString(action.payload?.callControlId);
      if (!callControlId) return state;
      const exists = state.calls.some((call) => call.callControlId === callControlId);
      if (!exists) return state;
      return {
        ...state,
        calls: upsertEntry(state.calls, {
          callControlId,
          state: 'ended',
          endedAt: action.payload?.endedAt || new Date().toISOString()
        })
      };
    }
    case VOICE_LIVE_CALL_PURGE: {
      const removedId = readString(action.payload);
      if (!removedId) return state;
      return { ...state, calls: state.calls.filter((call) => call.callControlId !== removedId) };
    }
    case VOICE_LIVE_CALLS_SET_SNAPSHOT: {
      const { payload } = action;
      let list = [];
      if (Array.isArray(payload)) {
        list = payload;
      } else if (Array.isArray(payload?.calls)) {
        list = payload.calls;
      }
      const calls = sortByRecencyDesc(
        list.map((entry) => normalizeVoiceLiveCallEntry(entry)).filter(Boolean)
      );
      let serverTime = null;
      if (!Array.isArray(payload) && typeof payload?.serverTime === 'string') {
        serverTime = payload.serverTime;
      }
      return { calls, serverTime };
    }
    case VOICE_LIVE_CALLS_CLEAR:
      return initialState;
    default:
      return state;
  }
}
